// src/pages/CookiePolicyPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Página oficial de Política de Cookies
 * Diseño rojinegro conmemorativo, explicaciones claras sobre Google Analytics
 * vía Google Tag Manager (GTM-WFJL46FN) y selector interactivo de consentimiento.
 */
export default function CookiePolicyPage() {
  const [currentConsent, setCurrentConsent] = useState('accepted');

  useEffect(() => {
    try {
      const saved = localStorage.getItem('xolofest_cookie_consent');
      if (saved) {
        setCurrentConsent(saved);
      }
    } catch (e) {}
    window.scrollTo(0, 0);
  }, []);

  const updatePreference = (newStatus) => {
    try {
      localStorage.setItem('xolofest_cookie_consent', newStatus);
      setCurrentConsent(newStatus);

      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: newStatus === 'accepted' ? 'granted' : 'denied'
        });
      }

      if (window.dataLayer && newStatus === 'accepted') {
        window.dataLayer.push({ event: 'cookie_consent_accepted' });
      }
    } catch (e) {}
  };

  return (
    <div className="cookie-policy-page py-4 py-md-5 position-relative overflow-hidden" style={{ minHeight: '88vh', background: 'transparent' }}>
      <div className="container position-relative" style={{ zIndex: 1 }}>
        {/* BOTÓN DE REGRESO ALINEADO */}
        <div className="row justify-content-center mb-3">
          <div className="col-12 col-md-10 col-lg-8">
            <Link
              to="/"
              className="btn btn-xolo-outline rounded-circle d-inline-flex align-items-center justify-content-center shadow"
              style={{
                width: '42px',
                height: '42px',
                padding: 0
              }}
              aria-label="Regresar al inicio"
              title="Regresar a la página principal"
            >
              <i className="bi bi-arrow-left fs-5"></i>
            </Link>
          </div>
        </div>

        {/* ENCABEZADO OFICIAL */}
        <div className="text-center mb-4">
          <div className="d-flex justify-content-center mb-3">
            <img
              src="/img/brand/logo-xolofest-horizontal.png"
              alt="XOLO FEST 2026 - Club Tijuana Xoloitzcuintles"
              style={{
                maxHeight: '75px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 6px 18px rgba(0, 0, 0, 0.7))'
              }}
            />
          </div>

          <h1 className="h2 text-white fw-black mb-2">
            Política de <span className="text-danger">Cookies</span>
          </h1>
          <p className="text-light opacity-90 mx-auto small" style={{ maxWidth: '640px' }}>
            Transparencia sobre el uso de tecnologías de almacenamiento y analítica en la plataforma de Xolo Fest 2026.
          </p>
        </div>

        {/* CONTENIDO PRINCIPAL */}
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8">
            <div
              className="p-4 p-md-5 rounded-4 shadow-2xl text-light"
              style={{
                backgroundColor: '#111116',
                border: '1px solid rgba(220, 38, 38, 0.3)'
              }}
            >
              {/* 1. ¿Qué son las cookies? */}
              <section className="mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                <h2 className="h5 text-white fw-bold mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle-fill text-danger"></i>
                  1. ¿Qué son las Cookies?
                </h2>
                <p className="small text-light opacity-90 lh-lg mb-0">
                  Las cookies son pequeños archivos de texto que los sitios web almacenan en tu dispositivo (computadora, tableta o teléfono móvil) mientras navegas. Permiten recordar tus preferencias, mantener la seguridad de las sesiones y recopilar información estadística agregada para optimizar el rendimiento y la experiencia del usuario.
                </p>
              </section>

              {/* 2. Cookies que utilizamos */}
              <section className="mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                <h2 className="h5 text-white fw-bold mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-shield-check text-danger"></i>
                  2. Cookies que utilizamos en Xolo Fest 2026
                </h2>

                <div className="card bg-black bg-opacity-60 border border-secondary border-opacity-25 p-3 rounded-3 mb-3">
                  <h3 className="h6 text-danger fw-bold mb-2">A. Cookies Técnicas y Esenciales (Obligatorias)</h3>
                  <p className="small text-light opacity-90 mb-2">
                    Son estrictamente necesarias para el funcionamiento del portal, la navegación segura y la prevención de fraudes o abuso. No recopilan datos con fines publicitarios.
                  </p>
                  <ul className="small text-light opacity-80 mb-0 ps-3">
                    <li><strong className="text-white">xolofest_cookie_consent:</strong> Almacena tu elección sobre las cookies en este dispositivo.</li>
                    <li><strong className="text-white">xf_session:</strong> Mantiene la sesión técnica segura para la validación y emisión de tu Pase Digital.</li>
                  </ul>
                </div>

                <div className="card bg-black bg-opacity-60 border border-secondary border-opacity-25 p-3 rounded-3">
                  <h3 className="h6 text-danger fw-bold mb-2">B. Cookies Analíticas (Google Analytics vía Google Tag Manager)</h3>
                  <p className="small text-light opacity-90 mb-2">
                    Utilizamos <strong>Google Analytics</strong> gestionado a través de <strong>Google Tag Manager (ID: GTM-WFJL46FN)</strong> para comprender cómo interactúa la afición con el sitio web:
                  </p>
                  <ul className="small text-light opacity-80 mb-0 ps-3">
                    <li>Medir visitas totales, páginas más consultadas y tiempo de navegación.</li>
                    <li>Identificar la fuente de llegada (aficionados que escanearon el QR de su boleto físico vs acceso web directo).</li>
                    <li>Toda la información se procesa de forma anónima y agregada, sin vincular datos personales sensibles.</li>
                  </ul>
                </div>
              </section>

              {/* 3. Consentimiento y Google Consent Mode v2 */}
              <section className="mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                <h2 className="h5 text-white fw-bold mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-toggle-on text-danger"></i>
                  3. Tu Consentimiento y Google Consent Mode v2
                </h2>
                <p className="small text-light opacity-90 lh-lg mb-0">
                  Implementamos el protocolo oficial <strong>Google Consent Mode v2</strong>. Esto significa que las cookies analíticas de Google Analytics permanecen desactivadas por defecto hasta que tú decidas pulsar <strong>"Aceptar"</strong> en nuestro aviso de cookies. Si eliges <strong>"Solo necesarias"</strong>, no se almacena ninguna cookie de seguimiento analítico.
                </p>
              </section>

              {/* 4. Panel Interactivo de Preferencias */}
              <section className="mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                <h2 className="h5 text-white fw-bold mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-sliders text-danger"></i>
                  4. Gestionar tus Preferencias en este Navegador
                </h2>
                <p className="small text-light opacity-90 mb-3">
                  Puedes cambiar o revocar tu consentimiento en cualquier momento desde este botón:
                </p>

                <div className="p-3 bg-black rounded-3 border border-danger border-opacity-30 d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3">
                  <div>
                    <span className="small d-block text-secondary">Estado actual de cookies analíticas:</span>
                    <strong className={currentConsent === 'accepted' ? 'text-success fs-6' : 'text-danger fs-6'}>
                      <i className={`bi ${currentConsent === 'accepted' ? 'bi-check-circle-fill' : 'bi-dash-circle-fill'} me-1`}></i>
                      {currentConsent === 'accepted' ? 'Aceptadas (Google Analytics activo)' : 'Solo necesarias (Google Analytics bloqueado)'}
                    </strong>
                  </div>

                  <div className="d-flex gap-2">
                    {currentConsent === 'accepted' ? (
                      <button
                        type="button"
                        className="btn btn-xolo-outline btn-sm px-3 py-2"
                        onClick={() => updatePreference('rejected')}
                      >
                        Cambiar a solo necesarias
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-xolo-primary btn-sm px-3 py-2 fw-bold"
                        onClick={() => updatePreference('accepted')}
                      >
                        Aceptar todas las cookies
                      </button>
                    )}
                  </div>
                </div>
              </section>

              {/* 5. Configuración en tu navegador */}
              <section className="mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                <h2 className="h5 text-white fw-bold mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-browser-chrome text-danger"></i>
                  5. ¿Cómo bloquear o eliminar cookies en tu navegador?
                </h2>
                <p className="small text-light opacity-90 mb-2">
                  Además de nuestro panel de control, puedes permitir, bloquear o eliminar las cookies instaladas en tu equipo mediante las opciones de configuración de tu navegador web:
                </p>
                <div className="d-flex flex-wrap gap-2 pt-2">
                  <a
                    href="https://support.google.com/chrome/answer/95647"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary text-light"
                  >
                    Google Chrome
                  </a>
                  <a
                    href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary text-light"
                  >
                    Apple Safari
                  </a>
                  <a
                    href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary text-light"
                  >
                    Mozilla Firefox
                  </a>
                  <a
                    href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary text-light"
                  >
                    Microsoft Edge
                  </a>
                </div>
              </section>

              {/* 6. Contacto y Responsable */}
              <section>
                <h2 className="h5 text-white fw-bold mb-2 d-flex align-items-center gap-2">
                  <i className="bi bi-envelope-fill text-danger"></i>
                  6. Contacto y Dudas
                </h2>
                <p className="small text-light opacity-90 mb-0">
                  Si tienes dudas o comentarios sobre el uso de cookies en el portal oficial de <strong>Xolo Fest 2026</strong>, puedes ponerte en contacto con el comité organizador del <strong>Club Tijuana Xoloitzcuintles de Caliente</strong> en las instalaciones del Estadio Caliente o a través de nuestros canales oficiales de atención al aficionado.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
